import { ColumnsType } from "antd/lib/table";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";

import { IInvoiceSearchResult } from "us.common/reducers/MainSearch/Interfaces";
import { IInvoice } from "us.common/components/MainSearch/Interfaces";
import {
  $Avatar,
  $Row,
  $Col,
  $Tag,
  $DateLabel,
  $AmountLabel,
  isViewed,
  $Drawer,
} from "us.common/components";
import { DateValues } from "../../Constants";
import { CaseTypes } from "us.common/constants";
import { ResultView } from "../ResultView";
import { getRouteUrl } from "us.helper";
import { EyeFilled, FileZipOutlined } from "us.icons";
import { RootState } from "us.helper/types";
import { MainSearchActions } from "us.common/actions";
import { MainCriterias } from "us.common/constants";
import DataArchive from "../DataArchive";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * @description - Columns component for invoice result view.
 * @author Samitha hewawasam <samitha@unicorn-solutions.com>
 * @since 10/03/2022
 * */
const Invoice: React.FC<IInvoice & PropsFromRedux> = (props) => {
  const {
    invoiceData,
    currentHit,
    totalSearchResult,
    viewedList,
    tabInfo,
    backNavigation, 
    isDataArchived,
    getArchivedCaseDetails,
    paginationChangeEvent,
    setNavigation,
    updateViewedList,
    getArchivedCases,
  } = props;
  const { t } = useTranslation();

  const [drawer, setDrawer] = useState({
    title: "",
    visible: false,
  });
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * Handle the row click event of invoice result view
   * @param e click event
   * @param exCaseNo invoice view table row caseNo value
   */
  const rowClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    exCaseNo: number,
    key: number,
    index: number
  ) => {
    if (isDataArchived) {
      getArchivedCaseDetails && getArchivedCaseDetails({ exCaseNo });
      setDrawer({
        title:
          t("US.COLLECTION.COMMON:COMMON.ARCHIVED_INVOICE") + `: ${exCaseNo}`,
        visible: true,
      });
    } else {
      updateViewedList &&
        updateViewedList({
          viewedList: [...viewedList, exCaseNo],
          viewedIndex: index,
          searchedCriteria: MainCriterias.INVOICE,
        });
      e.preventDefault();
      if (window.APP_MODULE == "collection") {
        if (e?.ctrlKey) {
          getRouteUrl.moduleRoute("collection", `/case/${exCaseNo}`);
        } else {
          setNavigation &&
            setNavigation({
              ...backNavigation,
              activeTab: MainCriterias.INVOICE,
              filters: tabInfo?.filterValues,
              canGoBack: true,
              currentHit,
              searchString: location.search,
            });
          navigate(`/case/${exCaseNo}`);
        }
      } else {
        getRouteUrl.moduleRoute("collection", `/case/${exCaseNo}`);
      }
    }
  };

  const columns: ColumnsType<any> = [
    {
      dataIndex: "key",
      key: "key",
      className: "item-type",
      render: (_caseNo, record, _index) => {
        return (
          <div>
            <$Avatar shape="square" size="small">
              {isViewed(viewedList, record.exCaseNo) ? <EyeFilled /> : `In`}
            </$Avatar>
          </div>
        );
      },
    },
    {
      dataIndex: "result",
      key: "result",
      render: (
        subcaseno_row: number,
        values: IInvoiceSearchResult,
        index: number
      ) => {
        const {
          exCaseNo,
          caseType,
          invoiceNumber,
          debtorNo,
          birthDate,
          address,
          phone,
          mainAmount,
          balance,
          creditorNo,
          creditorName,
          lastMessageActivityName,
          debtorName,
          key,
        } = values ?? {};
        return (
          <div>
            <$Row gutter={16}>
              <$Col span={10}>
                <div className="d-flex align-items-center title">
                  <a onClick={(e: any) => rowClick(e, exCaseNo, key, index)}>
                    <h3
                      className="mb-0"
                      data-testid="searchResults-invoiceExCaseNo"
                    >
                      {exCaseNo}
                    </h3>
                  </a>
                  {caseType === CaseTypes.OPEN && (
                    <$Tag className="ml-2 tag-status-open">{caseType}</$Tag>
                  )}
                  {caseType === CaseTypes.SENT_TO_COURT && (
                    <$Tag className="ml-2 tag-status-court">{caseType}</$Tag>
                  )}
                  {caseType === CaseTypes.CLOSED && (
                    <$Tag className="ml-2 tag-status-close">{caseType}</$Tag>
                  )}
                  {caseType === CaseTypes.MANUAL && (
                    <$Tag className="ml-2 tag-status-nextduedate">
                      {caseType}
                    </$Tag>
                  )}
                  {caseType === CaseTypes.SENTENCE_RECEIVED && (
                    <$Tag className="ml-2 tag-status-sentence">{caseType}</$Tag>
                  )}
                  <a onClick={(e: any) => rowClick(e, exCaseNo, key, index)}>
                    <h3
                      className="mb-0"
                      data-testid="searchResults-invoiceNumberAndName"
                    >
                      {`${t(
                        "US.COLLECTION.COMMON:COMMON.INVOICE_NO"
                      )} : ${invoiceNumber}`}
                    </h3>
                  </a>
                </div>
                <div className="d-flex align-items-start">
                  <div>
                    <label>{`${t(
                      "US.COLLECTION.COMMON:COMMON.DEBTOR"
                    )} :`}</label>
                  </div>
                  <div>
                    <div>
                      <strong className="mr-2">
                        {/* <a onClick={(e: any) => rowClick(e, exCaseNo, key, index)}>Click</a> */}
                        {debtorNo} {debtorName}
                      </strong>
                      <small className="mr-2">
                        {birthDate &&
                          dayjs
                            .utc(birthDate)
                            .isAfter(dayjs.utc(DateValues.minDate)) && (
                            <$DateLabel value={birthDate} />
                          )}
                      </small>
                    </div>
                    <div>
                      <small>
                        {[address, phone]
                          .flatMap((value) => (value ? [value] : []))
                          .join(" | ")}
                      </small>
                    </div>
                  </div>
                </div>
              </$Col>
              <$Col span={6}>
                <div className="d-flex align-items-start">
                  <div className="pr-5">
                    <label>
                      {t("US.COLLECTION.COMMON:COMMON.MAIN_AMOUNT")}
                    </label>
                    <h4 className="mb-0">
                      <$AmountLabel value={mainAmount} />
                    </h4>
                  </div>
                  <div>
                    <label>{t("US.COLLECTION.COMMON:COMMON.BALANCE")}</label>
                    <h4 className="mb-0">
                      <$AmountLabel value={balance} />
                    </h4>
                  </div>
                </div>
              </$Col>
              <$Col span={isDataArchived ? 6 : 8}>
                <div className="d-flex align-items-start">
                  <label
                    style={{
                      width: 150,
                      minWidth: 150,
                    }}
                    className="d-block pr-2 text-right"
                  >
                    {t("US.COLLECTION.COMMON:COMMON.CREDITOR")} :
                  </label>
                  <div>
                    {creditorNo} {creditorName}
                  </div>
                </div>
                {lastMessageActivityName && 
                  <div className="d-flex align-items-start">
                    <label
                      style={{
                        width: 150,
                        minWidth: 150,
                      }}
                      className="d-block pr-2 text-right"
                    >
                      {t("US.COLLECTION.COMMON:COMMON.LAST_MESSAGE_ACTIVITY")} :
                    </label>
                    <div>
                      {lastMessageActivityName}
                    </div>
                  </div>
                } 
              </$Col>
              {isDataArchived && (
                <$Col span={2}>
                  <span className="archived-icon text-muted ml-3">
                    <FileZipOutlined />
                  </span>
                </$Col>
              )}
            </$Row>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ResultView
        rowKey={"exCaseNo"}
        tabInfo={tabInfo}
        isDataArchived={isDataArchived}
        currentHit={currentHit}
        dataSource={invoiceData}
        paginationChangeEvent={paginationChangeEvent}
        tableClassName="result-table"
        tableColumns={columns}
        totalSearchResult={totalSearchResult}
        getArchivedData={(searchNumber: string) =>
          getArchivedCases && getArchivedCases({ searchNumber })
        }
      />
      <$Drawer
        open={drawer.visible}
        title={drawer.title}
        width={"600"}
        onClose={() => setDrawer({ title: "", visible: false })}
        destroyOnClose
      >
        <DataArchive />
      </$Drawer>
    </>
  );
};


const mapStateToProps = (state: RootState, ownProps: any) => {
  const { mainSearch } = state;
  const { backNavigation, viewedList, isDataArchived } = mainSearch;

  return {
    backNavigation,
    viewedList,
    isDataArchived,
    location: ownProps.location,  
    history: ownProps.history,  
  };
};

const { all, archiveCases, archiveCaseDetails } = MainSearchActions;

const mapDispatchToProps = {
  setNavigation: all.set,
  updateViewedList: all.updateViewedList,
  getArchivedCases: archiveCases.get,
  getArchivedCaseDetails: archiveCaseDetails.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Invoice);
